// Pedidos de venta — vista única de la entidad SalesTable de D365.
//
// Une lo que antes eran dos páginas: la consulta sobre Fabric (histórico,
// importes, facturas, adjuntos) y la creación/edición sobre Dataverse, que es
// síncrona. El desfase de ~15 min de Fabric lo tapa el overlay de
// usePedidosVentas: lo creado en las últimas horas se lee en vivo de F&O.
import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { usePedidosVentas } from '@/hooks/usePedidosVentas'
import { casaConHueco, opcionesConHueco } from '@/utils/filtroHuecos'
import { useFiltrosPersistentes } from '@/hooks/useFiltrosPersistentes'
import { pedidosVentasApi } from '@/services/api'
import { RUTA_DETALLE_PEDIDO_KEY, almacenSesion, rutaAReponer } from '@/utils/filtrosPersistentes'
import { exportarPedidosVenta } from '@/utils/exportarPedidosVenta'
import {
  catalogoDe, predicadoVariasGrafias, predicadoTextoAgregado,
} from '@/utils/filtroTexto'
import { AdjuntosModal } from '@/components/pedidosCompra/AdjuntosModal'
import { PageHeader } from '@/components/common/PageHeader'
import { RefreshButton } from '@/components/common/RefreshButton'
import { PAGE_KPIS } from '@/config/uiFlags'
import { PedidosVentasKpis } from '@/components/pedidos-ventas/PedidosVentasKpis'
import {
  PedidosVentasFiltros, EMPTY_FILTROS, contarActivos,
  type PedidosVentasFiltrosState,
} from '@/components/pedidos-ventas/PedidosVentasFiltros'
import { PedidosVentasTabla } from '@/components/pedidos-ventas/PedidosVentasTabla'
import { PedidoVentaForm } from '@/components/ordenes-servicio/PedidoVentaForm'
import { ElegirOrigenPedido } from '@/components/pedidos-ventas/ElegirOrigenPedido'
import { NuevoPedidoArchivoDrawer } from '@/components/pedidos-ventas/importar/NuevoPedidoArchivoDrawer'
import { SolicitarCancelacionModal } from '@/components/ordenes-servicio/SolicitarCancelacionModal'
import type {
  PedidoVentaRow, PedidoVentaSortKey, PedidoVentaResumen,
} from '@/types'
import { desdeInicial } from '@/utils/rangoPorDefecto'
import { useVariasEmpresas } from '@/hooks/useVariasEmpresas'

const PAGE_SIZE = 50

/** Posición de scroll de la lista, para volver del detalle donde se estaba. */
const SCROLL_KEY = 'pedidosVentas:scroll'

/** La fila de la tabla al shape que espera el formulario de Órdenes de servicio. */
function aResumen(r: PedidoVentaRow): PedidoVentaResumen {
  return {
    numero: r.salesid,
    empresa: r.dataareaid,
    cliente: r.custaccount,
    nombreCliente: r.nombreCliente ?? '',
    numeroPedidoCliente: '',
    fechaEnvio: r.fechaEntrega,
    estado: r.salesstatus ?? '',
    totalLineas: 0,
    importeTotal: r.importeTotal,
    moneda: r.moneda ?? '',
  }
}

export default function PedidosVentasPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const allowedEmpresas = useAllowedEmpresas()
  const isEmpresaAll = useIsEmpresaAll()
  // Mas de UNA empresa que elegir, no el rol `Empresa.All`.
  const variasEmpresas = useVariasEmpresas()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // Los filtros aguantan irse a otra pantalla y volver, durante la sesión. Hay
  // que leerlos AQUÍ, en los inicializadores: cuando se restaura, la URL todavía
  // está pelada en este render, y el rango de fechas es parámetro de la consulta
  // al backend — inicializarlo vacío dispararía ya una petición equivocada.
  // Las fechas SÍ se recuerdan: esta pantalla no calcula ningún rango por
  // defecto (vacío = sin filtro), así que reponer el que el usuario escribió no
  // puede vaciarle la tabla contra un default que se mueve solo.
  const { paramsIniciales: params0, olvidar } = useFiltrosPersistentes('pedidosVentas')

  // Volver al DETALLE donde se estaba: si se entra a la lista pelada y la sesión
  // recuerda un pedido abierto, se salta a él. Con cualquier parámetro en la URL
  // (enlace, marcador, «atrás» desde el detalle) manda la URL. Va DESPUÉS del hook
  // de filtros y en efecto de layout: su navegación es la última y no se ve la lista.
  // `saltando` frena la sincronización URL<-estado de abajo, que con su cierre
  // viejo pisaría el salto.
  const saltando = useRef(false)
  useLayoutEffect(() => {
    const ruta = rutaAReponer(RUTA_DETALLE_PEDIDO_KEY, location.search, almacenSesion())
    if (ruta) {
      saltando.current = true
      navigate(ruta, { replace: true })
    }
    // Sólo al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fechas (filtro backend). Como el resto, viven en la URL.
  const [desde, setDesde] = useState(desdeInicial(params0.get('desde')))
  const [hasta, setHasta] = useState(params0.get('hasta') ?? '')

  const [filtros, setFiltros] = useState<PedidosVentasFiltrosState>({
    ...EMPTY_FILTROS,
    // Los términos van en la URL separados por «|»: ni un nº de pedido ni un
    // BL ni un código de artículo lo contienen.
    pedido:   (params0.get('qp') ?? '').split('|').filter(Boolean),
    cliente:  (params0.get('qc') ?? '').split('|').filter(Boolean),
    articulo: (params0.get('qa') ?? '').split('|').filter(Boolean),
    // `q` es la clave del buscador único de antes. Se sigue leyendo para que un
    // enlace guardado hace meses filtre lo mismo que filtraba entonces; los
    // campos nuevos estrenan claves propias y no se pisan con ella.
    terminos: (params0.get('q') ?? '').split('|').filter(Boolean),
    empresa: params0.get('emp')     ?? '',
    estado:  params0.get('estado')  ?? '',
    familia: params0.get('familia') ?? '',
    soloPendientes: params0.get('pend') === '1',
    estadoCarga: (params0.get('carga') as PedidosVentasFiltrosState['estadoCarga']) ?? '',
    grupo: (params0.get('grupo') ?? '').split('|').filter(Boolean),
    conCarga: (params0.get('tcarga') as PedidosVentasFiltrosState['conCarga']) ?? '',
  })
  const [sortKey, setSortKey] = useState<PedidoVentaSortKey>(
    (params0.get('sort') as PedidoVentaSortKey) ?? 'fechaEntrega',
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    (params0.get('dir') as 'asc' | 'desc') ?? 'asc',
  )
  const [page, setPage] = useState(Number(params0.get('page')) || 1)

  // Modales
  const [adjuntosSalesid, setAdjuntosSalesid] = useState<string | null>(null)
  const [facturaAdj, setFacturaAdj] = useState<{ recid: string; factura: string } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  // Dos caminos de alta que acaban en sitios distintos: el manual escribe en
  // Dataverse y el de archivo se lo encarga a un flujo. Por eso se pregunta.
  const [eligiendoOrigen, setEligiendoOrigen] = useState(false)
  const [archivoOpen, setArchivoOpen] = useState(false)
  const [editando, setEditando] = useState<PedidoVentaResumen | null>(null)
  const [cancelando, setCancelando] = useState<PedidoVentaResumen | null>(null)
  const [exporting, setExporting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [avisos, setAvisos] = useState<string[]>([])

  const { rows, isLoading, isError, isFetching, refetch, invalidar } =
    usePedidosVentas(desde, hasta)

  // Todo el estado de consulta vive en la URL. Antes sólo subían las fechas, pero
  // el resto YA se leía al montar: el bucle estaba a medias. Cerrarlo es lo que
  // permite volver del detalle de un pedido y encontrarse la lista igual que se
  // dejó —y, de paso, que un enlace a la lista filtrada signifique algo.
  useEffect(() => {
    const p = new URLSearchParams()
    const poner = (clave: string, valor: string) => { if (valor) p.set(clave, valor) }
    poner('desde', desde)
    poner('hasta', hasta)
    poner('qp', filtros.pedido.join('|'))
    poner('qc', filtros.cliente.join('|'))
    poner('qa', filtros.articulo.join('|'))
    poner('q', filtros.terminos.join('|'))
    poner('emp', filtros.empresa)
    poner('estado', filtros.estado)
    poner('familia', filtros.familia)
    if (filtros.soloPendientes) p.set('pend', '1')
    poner('carga', filtros.estadoCarga)
    poner('grupo', filtros.grupo.join('|'))
    poner('tcarga', filtros.conCarga)
    if (sortKey !== 'fechaEntrega') p.set('sort', sortKey)
    if (sortDir !== 'asc') p.set('dir', sortDir)
    if (page > 1) p.set('page', String(page))
    // Sólo se escribe si cambia algo: si no, cada render dispararía una
    // navegación y el historial del navegador quedaría inservible.
    if (saltando.current) return
    if (p.toString() !== searchParams.toString()) {
      setSearchParams(p, { replace: true })
    }
  }, [desde, hasta, filtros, sortKey, sortDir, page, searchParams, setSearchParams])

  // Scroll de la lista: se guarda al salir y se repone al volver. El contenedor
  // que hace scroll es el <main> del layout, no la ventana.
  useEffect(() => {
    const main = document.querySelector('main')
    if (!main) return
    const guardado = sessionStorage.getItem(SCROLL_KEY)
    if (guardado) main.scrollTop = Number(guardado) || 0
    return () => { sessionStorage.setItem(SCROLL_KEY, String(main.scrollTop)) }
  }, [])

  function patchFiltros(patch: Partial<PedidosVentasFiltrosState>) {
    setFiltros(f => ({ ...f, ...patch }))
    setPage(1)
  }
  function clearFilters() {
    setFiltros(EMPTY_FILTROS)
    setPage(1)
    // El efecto de sincronización dejará la URL sin estas claves y eso ya borra
    // el recuerdo; `olvidar` lo hace explícito.
    olvidar()
  }

  // Opciones de filtro derivadas
  const allEstados = useMemo(() =>
    [...new Set(rows.map(r => r.salesstatus).filter(Boolean) as string[])].sort(), [rows])
  const allFamilias = useMemo(() =>
    [...new Set(rows.map(r => r.familia).filter(Boolean) as string[])].sort(), [rows])
  // El hueco entra como una opción más y sólo si alguna fila lo tiene: sin él,
  // los pedidos sin grupo desaparecerían al filtrar sin que nada lo dijera.
  const allGrupos = useMemo(
    () => opcionesConHueco(rows.map(r => r.grupoCliente), t('common.filtros.sinInformar')),
    [rows, t])

  // Catálogos de valores completos, para que pedir un pedido entero traiga ese y
  // no también el que lo lleva dentro (`F-100` contra `F-1000`). Salen de las
  // filas que hay en pantalla: lo que importa es si el término es un valor
  // entero de ESTE campo aquí.
  const catPedido = useMemo(() => {
    const c = catalogoDe(rows, r => r.salesid)
    for (const v of catalogoDe(rows, r => r.customerRef)) c.add(v)
    return c
  }, [rows])
  const catCliente = useMemo(() => {
    const c = catalogoDe(rows, r => r.custaccount)
    for (const v of catalogoDe(rows, r => r.nombreCliente)) c.add(v)
    return c
  }, [rows])

  const filtered = useMemo(() => {
    // Un predicado POR CAMPO, y todos tienen que cumplirse. Ese `&&` es la
    // corrección: antes los tres campos vivían en un solo texto y sólo cabía
    // preguntar por uno u otro, nunca por los dos a la vez.
    const casaPedido   = predicadoVariasGrafias(filtros.pedido, catPedido)
    const casaCliente  = predicadoVariasGrafias(filtros.cliente, catCliente)
    const casaArticulo = predicadoTextoAgregado(filtros.articulo)
    // El buscador antiguo sigue haciendo lo de siempre sobre el amasijo, para
    // los enlaces que aún lo traen. Es la única parte que conserva el defecto,
    // y a propósito: cambiarle el significado a un enlace ya repartido es peor.
    const lqs = filtros.terminos.map(x => x.toLowerCase())
    return rows.filter(r => {
      if (filtros.empresa && r.dataareaid !== filtros.empresa) return false
      if (filtros.estado  && r.salesstatus !== filtros.estado) return false
      if (filtros.familia && r.familia !== filtros.familia) return false
      if (!casaConHueco(r.grupoCliente, filtros.grupo)) return false
      // Éste SÍ esconde pedidos, al revés que `estadoCarga`: el dato está en la
      // cabecera, no en las líneas de carga.
      if (filtros.conCarga === 'si' && !r.tieneCarga) return false
      if (filtros.conCarga === 'no' && r.tieneCarga) return false
      // Un pedido del overlay no tiene cantidades todavía: se considera
      // pendiente por definición, no se esconde tras este filtro.
      if (filtros.soloPendientes && !r.pendienteSync && r.qtyPendiente <= 0) return false
      if (!casaPedido([r.salesid, r.customerRef])) return false
      if (!casaCliente([r.custaccount, r.nombreCliente])) return false
      if (!casaArticulo(r.articulosBusqueda)) return false
      if (lqs.length > 0) {
        const texto = [
          r.salesid, r.custaccount, r.nombreCliente, r.customerRef, r.articulosBusqueda,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!lqs.some(q => texto.includes(q))) return false
      }
      return true
    })
  }, [rows, filtros, catPedido, catCliente])

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      // Lo recién creado va siempre arriba, mande el orden que mande: si no,
      // se hunde entre decenas de miles de pedidos y el overlay no sirve de
      // nada al usuario que acaba de darle a guardar.
      if (!!a.pendienteSync !== !!b.pendienteSync) return a.pendienteSync ? -1 : 1
      const av = a[sortKey as keyof PedidoVentaRow]
      const bv = b[sortKey as keyof PedidoVentaRow]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv)
      return dir * String(av).localeCompare(String(bv), 'es-ES')
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage   = Math.min(page, totalPages)
  const paged      = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const activeFilters = contarActivos(filtros)

  function handleSort(k: PedidoVentaSortKey) {
    if (k === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir('asc') }
    setPage(1)
  }

  async function handleExport() {
    if (sorted.length === 0 || exporting) return
    setExporting(true)
    try {
      await exportarPedidosVenta(sorted, { isEmpresaAll, t, fmtArt })
    } finally {
      setExporting(false)
    }
  }

  function mostrarToast(msg: string, ms = 4000) {
    setToast(msg)
    window.setTimeout(() => setToast(null), ms)
  }

  function abrirNuevo() { setEditando(null); setEligiendoOrigen(true) }
  function cerrarForm() { setFormOpen(false); setEditando(null) }

  function onSaved(numero: string, advertencias: string[]) {
    const editaba = !!editando
    cerrarForm()
    invalidar()
    mostrarToast(t(editaba ? 'ordenesServicio.form.actualizado' : 'ordenesServicio.form.creado', { numero }))
    setAvisos(advertencias)
  }

  const pageBg = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const btnPrimary = 'px-4 min-h-[36px] rounded-lg text-[12px] font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors shadow-sm cursor-pointer'

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>
      <PageHeader
        dark={dark}
        title={t('salesOrders.title')}
        subtitle={
          <>
            {filtered.length} {t('common.of')} {rows.length} {t('common.orders')}
            {activeFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                · {t('common.activeFilter', { count: activeFilters })}
              </span>
            )}
          </>
        }
        actions={
          <>
            {isError && <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>}
            <RefreshButton dark={dark} onClick={refetch} isLoading={isFetching} />
            <button type="button" onClick={abrirNuevo} className={btnPrimary}>
              {t('salesOrders.new')}
            </button>
          </>
        }
      />

      {toast && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-[13px] text-emerald-500 font-medium shadow-sm">
          {toast}
        </div>
      )}

      {avisos.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-[13px] text-amber-500 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{t('ordenesServicio.form.advertenciasLineas')}</p>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                {avisos.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
            <button type="button" onClick={() => setAvisos([])} className="shrink-0 hover:underline">✕</button>
          </div>
        </div>
      )}

      {PAGE_KPIS.sales && (
        <PedidosVentasKpis dark={dark} pedidos={filtered} isLoading={isLoading} />
      )}

      <PedidosVentasFiltros
        dark={dark}
        value={filtros}
        onChange={patchFiltros}
        onClear={clearFilters}
        desde={desde}
        hasta={hasta}
        onDesde={setDesde}
        onHasta={setHasta}
        empresas={allowedEmpresas}
        showEmpresa={variasEmpresas}
        estados={allEstados}
        familias={allFamilias}
        grupos={allGrupos}
        resumen={`${filtered.length} ${t('common.orders')}`}
      />

      <PedidosVentasTabla
        dark={dark}
        isEmpresaAll={isEmpresaAll}
        pedidos={sorted}
        paged={paged}
        // La tabla usa esto para desplegar y resaltar la línea que casó, y eso
        // sólo puede salir de un término de ARTÍCULO: un nº de pedido casa con
        // la cabecera entera, no con una línea de dentro. Los del buscador
        // antiguo siguen entrando porque antes también desplegaban.
        terminos={[...filtros.articulo, ...filtros.terminos]}
        filtroEstadoCarga={filtros.estadoCarga}
        isLoading={isLoading}
        isError={isError}
        hasFilters={activeFilters > 0}
        page={safePage}
        totalPages={totalPages}
        sortKey={sortKey}
        sortDir={sortDir}
        exporting={exporting}
        onSort={handleSort}
        onSortDir={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
        onPage={setPage}
        onExport={handleExport}
        onClearFilters={clearFilters}
        onRetry={refetch}
        onVerAdjuntos={setAdjuntosSalesid}
        onVerAdjuntosFactura={(recid, factura) => setFacturaAdj({ recid, factura })}
        onEditar={(r) => { setEditando(aResumen(r)); setFormOpen(true) }}
        onCancelar={(r) => setCancelando(aResumen(r))}
      />

      {eligiendoOrigen && (
        <ElegirOrigenPedido
          dark={dark}
          onManual={() => { setEligiendoOrigen(false); setFormOpen(true) }}
          onArchivo={() => { setEligiendoOrigen(false); setArchivoOpen(true) }}
          onClose={() => setEligiendoOrigen(false)}
        />
      )}

      {archivoOpen && (
        <NuevoPedidoArchivoDrawer
          dark={dark}
          empresas={allowedEmpresas}
          onClose={() => setArchivoOpen(false)}
          onCursada={() => {
            // NO se invalida la lista: el pedido todavía no existe, y refrescar
            // daría a entender que debería estar ahí.
            mostrarToast(t('salesOrders.desdeArchivo.cursada.toast'), 6000)
          }}
        />
      )}

      {formOpen && (
        <PedidoVentaForm
          dark={dark}
          empresas={allowedEmpresas}
          pedido={editando}
          onClose={cerrarForm}
          onSaved={onSaved}
        />
      )}

      {cancelando && (
        <SolicitarCancelacionModal
          dark={dark}
          pedido={cancelando}
          onClose={() => setCancelando(null)}
          onSent={(mensaje) => { setCancelando(null); mostrarToast(mensaje, 5000) }}
        />
      )}

      {/* Modal adjuntos — pedido */}
      {adjuntosSalesid && (
        <AdjuntosModal
          dark={dark}
          pedidoId={adjuntosSalesid}
          queryKeyPrefix="venta-adjuntos"
          fetchAdjuntos={pedidosVentasApi.getAdjuntos}
          onClose={() => setAdjuntosSalesid(null)}
        />
      )}

      {/* Modal adjuntos — factura (reusa el visor, sin descarga por flujo) */}
      {facturaAdj && (
        <AdjuntosModal
          dark={dark}
          pedidoId={facturaAdj.factura}
          queryKeyPrefix={`venta-factura-adjuntos-${facturaAdj.recid}`}
          fetchAdjuntos={() => pedidosVentasApi.getAdjuntosFactura(facturaAdj.recid)}
          onClose={() => setFacturaAdj(null)}
        />
      )}
    </div>
  )
}
